<script setup>
// Criar ou editar um modelo do gateway: #chave, caixa e template aprovado, e a
// variável do SGP de cada {{n}} e de cada botão. As conferências (template
// aprovado, contagem de variáveis, botões) são do servidor; aqui só a prévia.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { createNexusApi, erroDaApi } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  // { sgp_id } para um novo; o modelo da API para editar.
  modelo: { type: Object, required: true },
  templates: { type: Array, required: true },
});
const emit = defineEmits(['salvo', 'cancelar']);

const { t } = useI18n({ messages });
const EXEMPLO_DE_CHAVE = 'fatura_vencendo';
const api = computed(() => createNexusApi(props.accountId));

const chave = ref(props.modelo.chave || '');
const template = ref(props.modelo.template || '');
const ativo = ref(props.modelo.ativo ?? true);
const parametros = ref([...(props.modelo.parametros || [])]);
const botoes = ref((props.modelo.botoes || []).map(b => b.variavel));
const salvando = ref(false);
const erro = ref('');

const escolhido = computed(() =>
  props.templates.find(opcao => opcao.valor === template.value)
);

// Trocar de template muda quantas variáveis e botões existem; o que já foi
// digitado fica, na mesma posição.
watch(
  escolhido,
  opcao => {
    if (!opcao) return;
    parametros.value = Array.from(
      { length: opcao.parametros },
      (_, i) => parametros.value[i] || ''
    );
    botoes.value = opcao.botoes.map((_, i) => botoes.value[i] || '');
  },
  { immediate: true }
);

const textoSgp = computed(() => {
  const nome = chave.value.trim().toLowerCase() || 'chave';
  const variaveis = [...parametros.value, ...botoes.value].map(
    v => `{${v.trim() || '?'}}`
  );
  return [`#${nome}`, ...variaveis].join('|');
});

// "{{1}}" montado aqui: chaves duplas dentro do template do Vue fecham a interpolação.
const rotuloDaVariavel = i =>
  t('NEUXS_NEXUS.GATEWAY.VARIAVEL', { n: `{{${i + 1}}}` });

const salva = async () => {
  salvando.value = true;
  erro.value = '';
  const dados = {
    chave: chave.value,
    template: template.value,
    parametros: parametros.value,
    botoes: botoes.value,
    ativo: ativo.value,
  };
  try {
    if (props.modelo.id) {
      await api.value.atualizaModelo(props.modelo.id, dados);
    } else {
      await api.value.criaModelo({ ...dados, sgp_id: props.modelo.sgp_id });
    }
    emit('salvo');
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.GATEWAY.ERRO_ACAO'));
  } finally {
    salvando.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
    @submit.prevent="salva"
  >
    <label class="flex flex-col gap-1 text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.GATEWAY.CHAVE') }}
      <input
        v-model="chave"
        type="text"
        :placeholder="EXEMPLO_DE_CHAVE"
        autocomplete="off"
        class="!mb-0"
      />
    </label>

    <label class="flex flex-col gap-1 text-sm text-n-slate-11">
      {{ t('NEUXS_NEXUS.GATEWAY.TEMPLATE') }}
      <p v-if="!templates.length" class="text-sm text-n-amber-11">
        {{ t('NEUXS_NEXUS.GATEWAY.SEM_TEMPLATES') }}
      </p>
      <select v-else v-model="template" class="!mb-0">
        <option value="">
          {{ t('NEUXS_NEXUS.GATEWAY.ESCOLHA_TEMPLATE') }}
        </option>
        <option
          v-for="opcao in templates"
          :key="opcao.valor"
          :value="opcao.valor"
          :disabled="Boolean(opcao.motivo)"
        >
          {{ opcao.rotulo }}
          {{
            opcao.motivo
              ? `— ${t('NEUXS_NEXUS.GATEWAY.NAO_SERVE', { motivo: opcao.motivo })}`
              : ''
          }}
        </option>
      </select>
    </label>

    <template v-if="escolhido">
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.GATEWAY.CORPO') }}
        </span>
        <p
          class="p-3 text-sm whitespace-pre-line rounded-lg bg-n-alpha-2 text-n-slate-12"
        >
          {{ escolhido.corpo }}
        </p>
      </div>
      <div class="grid gap-3 md:grid-cols-2">
        <label
          v-for="(_, i) in parametros"
          :key="`p${i}`"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ rotuloDaVariavel(i) }}
          <input
            v-model="parametros[i]"
            type="text"
            :placeholder="t('NEUXS_NEXUS.GATEWAY.VARIAVEL_DICA')"
            autocomplete="off"
            class="!mb-0"
          />
        </label>
        <label
          v-for="(botao, i) in escolhido.botoes"
          :key="`b${i}`"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{
            t('NEUXS_NEXUS.GATEWAY.BOTAO', {
              texto: botao.texto,
              tipo: botao.tipo,
            })
          }}
          <input
            v-model="botoes[i]"
            type="text"
            :placeholder="t('NEUXS_NEXUS.GATEWAY.VARIAVEL_DICA')"
            autocomplete="off"
            class="!mb-0"
          />
        </label>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-11">
          {{ t('NEUXS_NEXUS.GATEWAY.TEXTO_SGP') }}
        </span>
        <code class="text-xs break-all text-n-slate-12">{{ textoSgp }}</code>
      </div>
    </template>

    <label
      v-if="modelo.id"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <input v-model="ativo" type="checkbox" class="!mb-0" />
      {{ t('NEUXS_NEXUS.GATEWAY.ATIVO') }}
    </label>

    <p v-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>
    <div class="flex gap-2">
      <Button
        type="submit"
        :label="t('NEUXS_NEXUS.GATEWAY.SALVAR')"
        size="sm"
        :is-loading="salvando"
      />
      <Button
        :label="t('NEUXS_NEXUS.GATEWAY.CANCELAR')"
        size="sm"
        variant="ghost"
        color="slate"
        @click="emit('cancelar')"
      />
    </div>
  </form>
</template>
